import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { View } from 'react-native'
import { Section } from '@expo/html-elements'
import { Card, CardBody, CardHeader } from '../../components/Card'
import { H2, Text } from '../../components/TextComponents'
import { Col, Row } from '../../components/Row'
import { dsv } from '../../styles/defaults'
import { useUserListContext } from '../../hooks/context/useUserListContext'
import { LoadingScreen } from '../../components/LoadingScreen'
import { linkWithPlaceholder } from '../../utils/text'
import { useTranslation } from 'react-i18next'
import { RoleEnumSchema, RoleEnumType } from '@dpa/common/dist/prismaGenerated/inputTypeSchemas/RoleEnumSchema'
import { trpc } from '../../../App'
import { useAuthContext } from '../../hooks/context/useAuthContext'
import { Icon } from '../../components/Icon'

type Feedback = {
  type: 'success' | 'error'
  text: string
}

type ManagedUser = NonNullable<ReturnType<typeof useUserListContext>['userList']>[number]

type RoleDialogState = {
  userId: number
  roles: RoleEnumType[]
}

type ResetPasswordDialogState = {
  userId: number
  password: string
  confirmPassword: string
  showPassword: boolean
}

const roleOptions = RoleEnumSchema.options
const displayedRoleLimit = 3

const textOnDarkStyle: CSSProperties = {
  ...dsv.text,
  color: dsv.colors.connectGrau7,
}

const headingOnDarkStyle: CSSProperties = {
  ...dsv.heading,
  color: dsv.colors.connectGrau7,
}

const smallButtonStyle: CSSProperties = {
  ...dsv.text,
  borderWidth: 0,
  backgroundColor: dsv.colors.connectGrau,
  color: dsv.colors.white,
  padding: '8px 11px',
  cursor: 'pointer',
  minHeight: 36,
}

const prominentButtonStyle: CSSProperties = {
  ...smallButtonStyle,
  fontSize: 16,
  padding: '10px 14px',
}

const inputStyle: CSSProperties = {
  ...dsv.text,
  boxSizing: 'border-box',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: dsv.colors.connectGrau30,
  backgroundColor: dsv.colors.connectGrau7,
  color: dsv.colors.connectGrau,
  padding: '9px 10px',
  minHeight: 38,
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  fontSize: 14,
}

const fieldLabelStyle: CSSProperties = {
  ...textOnDarkStyle,
  display: 'block',
  marginBottom: 6,
  fontSize: 13,
  fontWeight: 700,
}

const labelColumnStyle: CSSProperties = {
  minWidth: 0,
}

const modalBackdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 1000,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  padding: '32px 24px',
  overflowY: 'auto',
}

const modalDialogStyle: CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: 860,
  backgroundColor: dsv.colors.connectGrau,
  padding: 28,
  boxShadow: '0 18px 48px rgba(0, 0, 0, 0.45)',
}

const formHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '18px 20px 16px',
  borderBottom: `1px solid ${dsv.colors.connectGrau70}`,
  boxShadow: `inset 0 4px 0 ${dsv.colors.GpJouleGelb}`,
}

const formBodyStyle: CSSProperties = {
  display: 'grid',
  gap: 18,
  padding: '18px 20px 20px',
}

const formGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))',
  gap: 12,
}

const formFooterStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: 10,
  paddingTop: 14,
  borderTop: `1px solid ${dsv.colors.connectGrau70}`,
}

const tableHeaderCellStyle: CSSProperties = {
  ...dsv.headingFont,
  color: dsv.colors.connectGrau,
  padding: 10,
  textAlign: 'left',
}

const tableCellStyle: CSSProperties = {
  ...dsv.text,
  color: dsv.colors.connectGrau,
  padding: 10,
  borderBottom: `1px solid ${dsv.colors.connectGrau30}`,
  verticalAlign: 'middle',
  fontSize: 14,
  lineHeight: '14px',
  height: 1,
}

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  backgroundColor: dsv.colors.connectGrau7,
}

const roleListStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
}

function EmployeeManagementStyles() {
  return (
    <style>
      {`
        .employee-row {
          transition: background-color 120ms ease, box-shadow 120ms ease;
        }

        .employee-row:hover {
          background-color: ${dsv.colors.base};
          box-shadow: inset 0 0 0 2px ${dsv.colors.GpJouleGelb50};
        }

        .employee-row--inactive:hover {
          background-color: ${dsv.colors.base2};
        }

        .employee-action-row {
          display: flex;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 8px;
        }

        .employee-action-row button {
          min-width: 118px;
        }

        @media (max-width: 980px) {
          .employee-table,
          .employee-table thead,
          .employee-table tbody,
          .employee-table tr,
          .employee-table th,
          .employee-table td {
            display: block;
            width: 100% !important;
          }

          .employee-table thead {
            display: none;
          }

          .employee-table tr {
            box-sizing: border-box;
            margin-bottom: 10px;
            border-left: 5px solid ${dsv.colors.GpJouleGelb};
          }

          .employee-table td {
            box-sizing: border-box;
            border-bottom: 0 !important;
            padding: 8px 10px !important;
          }

          .employee-action-row {
            justify-content: flex-start;
          }
        }
      `}
    </style>
  )
}

function ActionButton({
  children,
  onClick,
  danger = false,
  primary = false,
  prominent = false,
  disabled = false,
  ariaLabel,
  style,
}: {
  children: string
  onClick: () => void
  danger?: boolean
  primary?: boolean
  prominent?: boolean
  disabled?: boolean
  ariaLabel?: string
  style?: CSSProperties
}) {
  const buttonBaseStyle = prominent ? prominentButtonStyle : smallButtonStyle
  const foregroundColor = primary ? dsv.colors.connectGrau : dsv.colors.white

  return (
    <button
      type="button"
      aria-label={ariaLabel ?? children}
      disabled={disabled}
      onClick={onClick}
      style={{
        ...buttonBaseStyle,
        backgroundColor: disabled
          ? dsv.colors.connectGrau30
          : primary
            ? dsv.colors.GpJouleGelb
            : danger
              ? dsv.colors.error
              : dsv.colors.connectGrau,
        color: disabled ? dsv.colors.connectGrau : foregroundColor,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.8 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  )
}

function ModalShell({
  title,
  children,
  maxWidth = 860,
  onClose,
}: {
  title: string
  children: ReactNode
  maxWidth?: number
  onClose: () => void
}) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    closeButtonRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }

      if (event.key !== 'Tab' || dialogRef.current == null) {
        return
      }

      const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]'
      )
      const firstFocusableElement = focusableElements[0]
      const lastFocusableElement = focusableElements[focusableElements.length - 1]

      if (firstFocusableElement == null || lastFocusableElement == null) {
        return
      }

      if (event.shiftKey && document.activeElement === firstFocusableElement) {
        event.preventDefault()
        lastFocusableElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
        event.preventDefault()
        firstFocusableElement.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div style={modalBackdropStyle} onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="employee-dialog-title"
        style={{ ...modalDialogStyle, maxWidth }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ backgroundColor: dsv.colors.connectGrau }}>
          <div style={formHeaderStyle}>
            <H2 id="employee-dialog-title" style={{ ...headingOnDarkStyle, margin: 0, fontSize: 24 }}>
              {title}
            </H2>
            <button
              ref={closeButtonRef}
              type="button"
              aria-label={`${title} schließen`}
              onClick={onClose}
              style={{
                ...smallButtonStyle,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              Schließen
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}

function DialogPersonHeader({ user }: { user: ManagedUser }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 3,
        padding: '12px 14px',
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        borderLeft: `4px solid ${dsv.colors.GpJouleGelb}`,
      }}
    >
      <Text style={{ ...textOnDarkStyle, fontSize: 16, fontWeight: '800' }}>{user.name}</Text>
      <Text style={{ color: dsv.colors.connectGrau30, fontSize: 13 }}>{user.email}</Text>
    </div>
  )
}

function SelectableRoleChip({
  role,
  selected,
  onClick,
}: {
  role: RoleEnumType
  selected: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      style={{
        ...dsv.text,
        minHeight: 34,
        padding: '7px 10px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: selected ? dsv.colors.GpJouleGelb : dsv.colors.connectGrau30,
        backgroundColor: selected ? dsv.colors.GpJouleGelb30 : dsv.colors.base,
        color: dsv.colors.connectGrau,
        fontSize: 13,
        fontWeight: selected ? 700 : 500,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {selected && <Icon name="check" size={13} color={dsv.colors.connectGrau} />}
      {t(role)}
    </button>
  )
}

function RolePill({
  role,
  muted,
}: {
  role: RoleEnumType
  muted?: boolean
}) {
  const { t } = useTranslation()

  return (
    <span
      style={{
        ...dsv.text,
        minHeight: 24,
        padding: '4px 8px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: muted ? dsv.colors.connectGrau30 : dsv.colors.GpJouleGelb50,
        backgroundColor: muted ? dsv.colors.base3 : dsv.colors.GpJouleGelb30,
        color: muted ? dsv.colors.connectGrau70 : dsv.colors.connectGrau,
        fontSize: 12,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {t(role)}
    </span>
  )
}

function RoleChips({
  roles,
  muted,
}: {
  roles: RoleEnumType[]
  muted?: boolean
}) {
  const { t } = useTranslation()
  const visibleRoles = roles.slice(0, displayedRoleLimit)
  const remainingRoles = roles.slice(displayedRoleLimit)
  const remainingCount = remainingRoles.length

  if (roles.length === 0) {
    return <Text style={{ color: dsv.colors.connectGrau70, fontSize: 13 }}>Keine Rollen</Text>
  }

  return (
    <div style={roleListStyle}>
      {visibleRoles.map((role) => (
        <RolePill key={role} role={role} muted={muted} />
      ))}
      {remainingCount > 0 && (
        <span
          title={remainingRoles.map((role) => t(role)).join(', ')}
          style={{
            ...dsv.text,
            minHeight: 24,
            padding: '4px 8px',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: dsv.colors.connectGrau30,
            backgroundColor: muted ? dsv.colors.base3 : dsv.colors.base,
            color: dsv.colors.connectGrau70,
            fontSize: 12,
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          +{remainingCount} weitere
        </span>
      )}
    </div>
  )
}

function StatusBadge({ disabled }: { disabled: boolean }) {
  return (
    <span
      aria-label={disabled ? 'Status inaktiv' : 'Status aktiv'}
      style={{
        ...dsv.text,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 64,
        padding: '4px 8px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: disabled ? dsv.colors.connectGrau30 : '#96A800',
        backgroundColor: disabled ? dsv.colors.base3 : '#EEF6C8',
        color: disabled ? dsv.colors.connectGrau70 : dsv.colors.connectGrau,
        fontSize: 12,
        fontWeight: 800,
      }}
    >
      {disabled ? 'Inaktiv' : 'Aktiv'}
    </span>
  )
}

function FeedbackText({ feedback }: { feedback?: Feedback | null }) {
  if (feedback == null) {
    return null
  }

  return (
    <Text
      style={{
        color: feedback.type === 'success' ? dsv.colors.success : dsv.colors.error,
        fontSize: 13,
      }}
    >
      {feedback.text}
    </Text>
  )
}

function EditRolesDialog({
  user,
  draftRoles,
  feedback,
  isSaving,
  onToggleRole,
  onSave,
  onClose,
}: {
  user: ManagedUser
  draftRoles: RoleEnumType[]
  feedback?: Feedback | null
  isSaving: boolean
  onToggleRole: (role: RoleEnumType) => void
  onSave: () => void
  onClose: () => void
}) {
  return (
    <ModalShell title="Rollen bearbeiten" onClose={onClose}>
      <div style={formBodyStyle}>
        <DialogPersonHeader user={user} />
        <div style={roleListStyle}>
          {roleOptions.map((role) => (
            <SelectableRoleChip
              key={role}
              role={role}
              selected={draftRoles.includes(role)}
              onClick={() => onToggleRole(role)}
            />
          ))}
        </div>
        <FeedbackText feedback={feedback} />
        <div style={formFooterStyle}>
          <ActionButton onClick={onClose}>
            Abbrechen
          </ActionButton>
          <ActionButton primary prominent disabled={isSaving} onClick={onSave}>
            Rollen speichern
          </ActionButton>
        </div>
      </div>
    </ModalShell>
  )
}

function ResetPasswordDialog({
  user,
  password,
  confirmPassword,
  showPassword,
  feedback,
  isSaving,
  onPasswordChange,
  onConfirmPasswordChange,
  onShowPasswordChange,
  onSave,
  onClose,
}: {
  user: ManagedUser
  password: string
  confirmPassword: string
  showPassword: boolean
  feedback?: Feedback | null
  isSaving: boolean
  onPasswordChange: (password: string) => void
  onConfirmPasswordChange: (password: string) => void
  onShowPasswordChange: (showPassword: boolean) => void
  onSave: () => void
  onClose: () => void
}) {
  const passwordTooShort = password.length > 0 && password.length < 8
  const passwordsDoNotMatch = confirmPassword.length > 0 && password !== confirmPassword
  const passwordInputId = `set-password-${user.id}`
  const confirmPasswordInputId = `set-password-confirm-${user.id}`
  const passwordHelpId = `set-password-help-${user.id}`
  const passwordErrorId = `set-password-error-${user.id}`
  const passwordError =
    passwordTooShort
      ? 'Das Passwort muss mindestens 8 Zeichen lang sein.'
      : passwordsDoNotMatch
        ? 'Die Passwörter stimmen nicht überein.'
        : undefined
  const canSubmit = password.length >= 8 && password === confirmPassword

  return (
    <ModalShell title="Passwort setzen" maxWidth={620} onClose={onClose}>
      <div style={formBodyStyle}>
        <DialogPersonHeader user={user} />
        <Text style={textOnDarkStyle}>
          Das Passwort wird direkt auf den eingetragenen Wert gesetzt. Die Person kann sich anschließend mit diesem
          neuen Passwort anmelden.
        </Text>
        <Text style={{ ...textOnDarkStyle, color: dsv.colors.connectGrau30, fontSize: 13 }}>
          Aktuell gibt es keinen automatischen Passwort-Reset per E-Mail.
        </Text>
        <label style={labelColumnStyle}>
          <Text style={fieldLabelStyle} nativeID={`${passwordInputId}-label`}>
            Neues Passwort
          </Text>
          <input
            id={passwordInputId}
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            placeholder="Mindestens 8 Zeichen"
            type={showPassword ? 'text' : 'password'}
            style={inputStyle}
            aria-labelledby={`${passwordInputId}-label`}
            aria-describedby={passwordError == null ? passwordHelpId : `${passwordHelpId} ${passwordErrorId}`}
            aria-invalid={passwordTooShort}
            autoFocus
          />
        </label>
        <label style={labelColumnStyle}>
          <Text style={fieldLabelStyle} nativeID={`${confirmPasswordInputId}-label`}>
            Passwort bestätigen
          </Text>
          <input
            id={confirmPasswordInputId}
            value={confirmPassword}
            onChange={(event) => onConfirmPasswordChange(event.target.value)}
            placeholder="Passwort erneut eingeben"
            type={showPassword ? 'text' : 'password'}
            style={inputStyle}
            aria-labelledby={`${confirmPasswordInputId}-label`}
            aria-describedby={passwordsDoNotMatch ? passwordErrorId : passwordHelpId}
            aria-invalid={passwordsDoNotMatch}
          />
        </label>
        <label
          style={{
            ...textOnDarkStyle,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={showPassword}
            onChange={(event) => onShowPasswordChange(event.target.checked)}
          />
          Passwort anzeigen
        </label>
        <Text id={passwordHelpId} style={{ color: dsv.colors.connectGrau30, fontSize: 13 }}>
          Mindestens 8 Zeichen.
        </Text>
        {passwordError != null && (
          <Text id={passwordErrorId} style={{ color: dsv.colors.error, fontSize: 13 }}>
            {passwordError}
          </Text>
        )}
        <FeedbackText feedback={feedback} />
        <div style={formFooterStyle}>
          <ActionButton onClick={onClose}>
            Abbrechen
          </ActionButton>
          <ActionButton primary prominent disabled={isSaving || !canSubmit} onClick={onSave}>
            Passwort setzen
          </ActionButton>
        </div>
      </div>
    </ModalShell>
  )
}

function ConfirmDeactivateDialog({
  user,
  feedback,
  isSaving,
  onConfirm,
  onClose,
}: {
  user: ManagedUser
  feedback?: Feedback | null
  isSaving: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <ModalShell title="Person deaktivieren" maxWidth={620} onClose={onClose}>
      <div style={formBodyStyle}>
        <DialogPersonHeader user={user} />
        <Text style={textOnDarkStyle}>
          Der Login wird deaktiviert. Bestehende Verknüpfungen in Akten und Projekten bleiben erhalten.
        </Text>
        <FeedbackText feedback={feedback} />
        <div style={formFooterStyle}>
          <ActionButton onClick={onClose}>
            Abbrechen
          </ActionButton>
          <ActionButton danger prominent disabled={isSaving} onClick={onConfirm}>
            Deaktivieren
          </ActionButton>
        </div>
      </div>
    </ModalShell>
  )
}

function EmployeeActions({
  user,
  isInactive,
  canDeactivate,
  isUpdatingStatus,
  onEditRoles,
  onResetPassword,
  onActivate,
  onDeactivate,
}: {
  user: ManagedUser
  isInactive: boolean
  canDeactivate: boolean
  isUpdatingStatus: boolean
  onEditRoles: () => void
  onResetPassword: () => void
  onActivate: () => void
  onDeactivate: () => void
}) {
  if (isInactive) {
    return (
      <div className="employee-action-row">
        <ActionButton
          primary
          disabled={isUpdatingStatus}
          ariaLabel={`${user.name} aktivieren`}
          onClick={onActivate}
        >
          Person aktivieren
        </ActionButton>
      </div>
    )
  }

  return (
    <div className="employee-action-row">
      <ActionButton ariaLabel={`Rollen von ${user.name} bearbeiten`} onClick={onEditRoles}>
        Rollen bearbeiten
      </ActionButton>
      <ActionButton ariaLabel={`Passwort von ${user.name} setzen`} onClick={onResetPassword}>
        Passwort setzen
      </ActionButton>
      <ActionButton
        danger
        disabled={isUpdatingStatus || !canDeactivate}
        ariaLabel={`${user.name} deaktivieren`}
        onClick={onDeactivate}
        style={{ opacity: canDeactivate ? 0.86 : 0.55 }}
      >
        Person deaktivieren
      </ActionButton>
    </div>
  )
}

function areRolesEqual(left: RoleEnumType[], right: RoleEnumType[]) {
  if (left.length !== right.length) {
    return false
  }

  return left.every((role) => right.includes(role))
}

function userRoles(user: ManagedUser) {
  return user.UserRoles.map(({ role }) => role)
}

export function Mitarbeiter() {
  const { isLoading, userList } = useUserListContext()
  const { authCtx, hasRole } = useAuthContext()
  const isAdmin = hasRole('Admin')
  const utils = trpc.useContext()
  const dialogTriggerRef = useRef<HTMLElement | null>(null)

  const visibleUsers = useMemo(
    () => (userList || []).filter((user) => !(user.id === 1 && user.name === 'BC Syncer')),
    [userList]
  )
  const userById = useMemo(
    () => Object.fromEntries(visibleUsers.map((user) => [user.id, user])),
    [visibleUsers]
  )

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newRoles, setNewRoles] = useState<RoleEnumType[]>([])
  const [roleDialog, setRoleDialog] = useState<RoleDialogState | null>(null)
  const [resetPasswordDialog, setResetPasswordDialog] = useState<ResetPasswordDialogState | null>(null)
  const [deactivateUserId, setDeactivateUserId] = useState<number | null>(null)
  const [createFeedback, setCreateFeedback] = useState<Feedback | null>(null)
  const [userFeedback, setUserFeedback] = useState<Record<number, Feedback | null>>({})

  const clearUserFeedback = useCallback((userId: number) => {
    setUserFeedback((prev) => ({ ...prev, [userId]: null }))
  }, [])

  const rememberDialogTrigger = useCallback(() => {
    dialogTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
  }, [])

  const restoreDialogTriggerFocus = useCallback(() => {
    window.setTimeout(() => dialogTriggerRef.current?.focus(), 0)
  }, [])

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false)
    setCreateFeedback(null)
    restoreDialogTriggerFocus()
  }, [restoreDialogTriggerFocus])

  const closeRoleDialog = useCallback(() => {
    setRoleDialog(null)
    restoreDialogTriggerFocus()
  }, [restoreDialogTriggerFocus])

  const closeResetPasswordDialog = useCallback(() => {
    setResetPasswordDialog(null)
    restoreDialogTriggerFocus()
  }, [restoreDialogTriggerFocus])

  const closeDeactivateDialog = useCallback(() => {
    setDeactivateUserId(null)
    restoreDialogTriggerFocus()
  }, [restoreDialogTriggerFocus])

  const registerUser = trpc.auth.register.useMutation({
    onSuccess: async () => {
      setCreateFeedback({ type: 'success', text: 'Person wurde angelegt.' })
      setNewName('')
      setNewEmail('')
      setNewPassword('')
      setNewRoles([])
      setCreateModalOpen(false)
      restoreDialogTriggerFocus()
      await utils.userList.invalidate()
    },
    onError: (error) => setCreateFeedback({ type: 'error', text: error.message }),
  })

  const updateRoles = trpc.auth.updateUserRoles.useMutation({
    onSuccess: async (_data, variables) => {
      closeRoleDialog()
      setUserFeedback((prev) => ({
        ...prev,
        [variables.userId]: { type: 'success', text: 'Rollen wurden gespeichert.' },
      }))
      await utils.userList.invalidate()
    },
    onError: (error, variables) => {
      setUserFeedback((prev) => ({ ...prev, [variables.userId]: { type: 'error', text: error.message } }))
    },
  })

  const resetPassword = trpc.auth.resetUserPassword.useMutation({
    onSuccess: (_data, variables) => {
      closeResetPasswordDialog()
      setUserFeedback((prev) => ({
        ...prev,
        [variables.userId]: { type: 'success', text: 'Passwort wurde gesetzt.' },
      }))
    },
    onError: (error, variables) => {
      setUserFeedback((prev) => ({ ...prev, [variables.userId]: { type: 'error', text: error.message } }))
    },
  })

  const updateDisabled = trpc.auth.updateUserDisabled.useMutation({
    onSuccess: async (data) => {
      const disabled = data.disabledAt != null
      closeDeactivateDialog()
      setUserFeedback((prev) => ({
        ...prev,
        [data.id]: {
          type: 'success',
          text: disabled ? 'Person wurde deaktiviert.' : 'Person wurde aktiviert.',
        },
      }))
      await utils.userList.invalidate()
    },
    onError: (error, variables) => {
      setUserFeedback((prev) => ({ ...prev, [variables.userId]: { type: 'error', text: error.message } }))
    },
  })

  const toggleNewRole = useCallback((role: RoleEnumType) => {
    setCreateFeedback(null)
    setNewRoles((prev) => (prev.includes(role) ? prev.filter((currentRole) => currentRole !== role) : [...prev, role]))
  }, [])

  const toggleDialogRole = useCallback((role: RoleEnumType) => {
    setRoleDialog((current) => {
      if (current == null) {
        return current
      }

      return {
        ...current,
        roles: current.roles.includes(role)
          ? current.roles.filter((currentRole) => currentRole !== role)
          : [...current.roles, role],
      }
    })
  }, [])

  const createUser = useCallback(() => {
    if (newName.trim() === '' || newEmail.trim() === '' || newPassword === '') {
      setCreateFeedback({ type: 'error', text: 'Bitte Name, E-Mail und Passwort ausfüllen.' })
      return
    }

    if (newPassword.length < 8) {
      setCreateFeedback({ type: 'error', text: 'Das Passwort muss mindestens 8 Zeichen lang sein.' })
      return
    }

    registerUser.mutate({
      name: newName.trim(),
      email: newEmail.trim(),
      password: newPassword,
      roles: newRoles,
    })
  }, [newEmail, newName, newPassword, newRoles, registerUser])

  const saveDialogRoles = useCallback(() => {
    if (roleDialog == null) {
      return
    }

    updateRoles.mutate({
      userId: roleDialog.userId,
      roles: roleDialog.roles,
    })
  }, [roleDialog, updateRoles])

  const saveDialogPassword = useCallback(() => {
    if (resetPasswordDialog == null) {
      return
    }

    if (resetPasswordDialog.password.length < 8) {
      setUserFeedback((prev) => ({
        ...prev,
        [resetPasswordDialog.userId]: { type: 'error', text: 'Das Passwort muss mindestens 8 Zeichen lang sein.' },
      }))
      return
    }

    if (resetPasswordDialog.password !== resetPasswordDialog.confirmPassword) {
      setUserFeedback((prev) => ({
        ...prev,
        [resetPasswordDialog.userId]: { type: 'error', text: 'Die Passwörter stimmen nicht überein.' },
      }))
      return
    }

    resetPassword.mutate({ userId: resetPasswordDialog.userId, password: resetPasswordDialog.password })
  }, [resetPassword, resetPasswordDialog])

  const roleDialogUser = roleDialog == null ? undefined : userById[roleDialog.userId]
  const resetPasswordUser = resetPasswordDialog == null ? undefined : userById[resetPasswordDialog.userId]
  const deactivateUser = deactivateUserId == null ? undefined : userById[deactivateUserId]
  const persistedRoleDialogRoles = roleDialogUser == null ? [] : userRoles(roleDialogUser)
  const roleDialogHasChanges = roleDialog != null && !areRolesEqual(roleDialog.roles, persistedRoleDialogRoles)

  return (
    <Section>
      <EmployeeManagementStyles />
      <Row>
        <Col cols={12}>
          <Card>
            <CardHeader>Mitarbeitende</CardHeader>
            <CardBody>
              {isLoading && <LoadingScreen active />}
              {!isLoading && (
                <>
                  <View
                    style={{
                      marginBottom: isAdmin ? 40 : 18,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <View style={{ display: 'flex', gap: 6 }}>
                      <Text style={textOnDarkStyle}>
                        {visibleUsers.length} {visibleUsers.length === 1 ? 'Eintrag' : 'Einträge'}
                      </Text>
                      {!createModalOpen && <FeedbackText feedback={createFeedback} />}
                    </View>
                    {isAdmin && (
                      <ActionButton
                        prominent
                        primary
                        onClick={() => {
                          rememberDialogTrigger()
                          setCreateFeedback(null)
                          setCreateModalOpen(true)
                        }}
                      >
                        Mitarbeitende hinzufügen
                      </ActionButton>
                    )}
                  </View>

                  <table className="employee-table" style={tableStyle}>
                    <thead style={{ backgroundColor: dsv.colors.GpJouleGelb }}>
                      <tr>
                        <th style={{ ...tableHeaderCellStyle, width: 92 }}>Status</th>
                        <th style={{ ...tableHeaderCellStyle, width: '28%' }}>Mitarbeitende</th>
                        <th style={tableHeaderCellStyle}>Rollen</th>
                        {isAdmin && (
                          <th style={{ ...tableHeaderCellStyle, width: 410, textAlign: 'right' }}>Aktionen</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleUsers.map((user) => {
                        const persistedRoles = userRoles(user)
                        const canDeactivate = authCtx?.id !== user.id
                        const disabled = user.disabledAt != null

                        return (
                          <tr
                            key={user.id}
                            className={`employee-row ${disabled ? 'employee-row--inactive' : ''}`}
                            style={{
                              backgroundColor: disabled ? dsv.colors.base3 : dsv.colors.connectGrau7,
                              boxShadow: disabled ? `inset 5px 0 0 ${dsv.colors.connectGrau70}` : undefined,
                            }}
                          >
                            <td style={tableCellStyle}>
                              <StatusBadge disabled={disabled} />
                            </td>
                            <td style={tableCellStyle}>
                              <div style={{ display: 'grid', gap: 3 }}>
                                <span
                                  style={{
                                    ...dsv.headingFont,
                                    color: disabled ? dsv.colors.connectGrau70 : dsv.colors.connectGrau,
                                  }}
                                >
                                  {user.name}
                                </span>
                                <span style={{ opacity: disabled ? 0.62 : 1 }}>
                                  {linkWithPlaceholder(user.email, 'mailTo')}
                                </span>
                              </div>
                            </td>
                            <td style={tableCellStyle}>
                              <RoleChips roles={persistedRoles} muted={disabled} />
                            </td>
                            {isAdmin && (
                              <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                                <EmployeeActions
                                  user={user}
                                  isInactive={disabled}
                                  canDeactivate={canDeactivate}
                                  isUpdatingStatus={updateDisabled.isLoading}
                                  onEditRoles={() => {
                                    rememberDialogTrigger()
                                    clearUserFeedback(user.id)
                                    setRoleDialog({
                                      userId: user.id,
                                      roles: persistedRoles,
                                    })
                                  }}
                                  onResetPassword={() => {
                                    rememberDialogTrigger()
                                    clearUserFeedback(user.id)
                                    setResetPasswordDialog({
                                      userId: user.id,
                                      password: '',
                                      confirmPassword: '',
                                      showPassword: false,
                                    })
                                  }}
                                  onActivate={() => updateDisabled.mutate({ userId: user.id, disabled: false })}
                                  onDeactivate={() => {
                                    rememberDialogTrigger()
                                    clearUserFeedback(user.id)
                                    setDeactivateUserId(user.id)
                                  }}
                                />
                                <div style={{ marginTop: 8 }}>
                                  <FeedbackText feedback={userFeedback[user.id]} />
                                </div>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                      {visibleUsers.length === 0 && (
                        <tr>
                          <td colSpan={isAdmin ? 4 : 3} style={tableCellStyle}>
                            Keine Einträge gefunden
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {createModalOpen && (
        <ModalShell title="Mitarbeitende hinzufügen" onClose={closeCreateModal}>
          <div style={formBodyStyle}>
            <div style={formGridStyle}>
              <label style={labelColumnStyle}>
                <Text style={fieldLabelStyle}>Name</Text>
                <input
                  value={newName}
                  onChange={(event) => {
                    setCreateFeedback(null)
                    setNewName(event.target.value)
                  }}
                  placeholder="Max Mustermann"
                  style={inputStyle}
                />
              </label>
              <label style={labelColumnStyle}>
                <Text style={fieldLabelStyle}>E-Mail</Text>
                <input
                  value={newEmail}
                  onChange={(event) => {
                    setCreateFeedback(null)
                    setNewEmail(event.target.value)
                  }}
                  placeholder="max@gp-joule.de"
                  type="email"
                  style={inputStyle}
                />
              </label>
              <label style={labelColumnStyle}>
                <Text style={fieldLabelStyle}>Passwort</Text>
                <input
                  value={newPassword}
                  onChange={(event) => {
                    setCreateFeedback(null)
                    setNewPassword(event.target.value)
                  }}
                  placeholder="Mindestens 8 Zeichen"
                  type="password"
                  style={inputStyle}
                />
              </label>
            </div>

            <div>
              <Text style={fieldLabelStyle}>Rollen</Text>
              <div style={roleListStyle}>
                {roleOptions.map((role) => (
                  <SelectableRoleChip
                    key={role}
                    role={role}
                    selected={newRoles.includes(role)}
                    onClick={() => toggleNewRole(role)}
                  />
                ))}
              </div>
            </div>

            <FeedbackText feedback={createFeedback} />

            <div style={formFooterStyle}>
              <ActionButton onClick={closeCreateModal}>
                Abbrechen
              </ActionButton>
              <ActionButton primary prominent disabled={registerUser.isLoading} onClick={createUser}>
                Anlegen
              </ActionButton>
            </div>
          </div>
        </ModalShell>
      )}

      {roleDialog != null && roleDialogUser != null && (
        <EditRolesDialog
          user={roleDialogUser}
          draftRoles={roleDialog.roles}
          feedback={userFeedback[roleDialog.userId]}
          isSaving={updateRoles.isLoading || !roleDialogHasChanges}
          onToggleRole={toggleDialogRole}
          onSave={saveDialogRoles}
          onClose={closeRoleDialog}
        />
      )}

      {resetPasswordDialog != null && resetPasswordUser != null && (
        <ResetPasswordDialog
          user={resetPasswordUser}
          password={resetPasswordDialog.password}
          confirmPassword={resetPasswordDialog.confirmPassword}
          showPassword={resetPasswordDialog.showPassword}
          feedback={userFeedback[resetPasswordDialog.userId]}
          isSaving={resetPassword.isLoading}
          onPasswordChange={(password) =>
            setResetPasswordDialog((current) => current == null ? current : { ...current, password })
          }
          onConfirmPasswordChange={(confirmPassword) =>
            setResetPasswordDialog((current) => current == null ? current : { ...current, confirmPassword })
          }
          onShowPasswordChange={(showPassword) =>
            setResetPasswordDialog((current) => current == null ? current : { ...current, showPassword })
          }
          onSave={saveDialogPassword}
          onClose={closeResetPasswordDialog}
        />
      )}

      {deactivateUser != null && (
        <ConfirmDeactivateDialog
          user={deactivateUser}
          feedback={userFeedback[deactivateUser.id]}
          isSaving={updateDisabled.isLoading}
          onConfirm={() => updateDisabled.mutate({ userId: deactivateUser.id, disabled: true })}
          onClose={closeDeactivateDialog}
        />
      )}
    </Section>
  )
}
